import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Wrench, Clock, ShieldCheck, Award } from "lucide-react";

const REPAIR_IMG = "https://images.unsplash.com/photo-1550041473-d296a3a8a18a?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85";

export default function Repairs() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", device: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads", { type: "repair", ...form });
      toast.success("Repair request received — we'll call you within 1 hour.");
      setForm({ name: "", phone: "", email: "", device: "", message: "" });
    } catch {
      toast.error("Could not send request. Please try again.");
    } finally { setSubmitting(false); }
  };
  return (
    <div data-testid="repairs-page">
      <section className="relative h-[400px] flex items-center" data-testid="repairs-hero">
        <img src={REPAIR_IMG} alt="repair" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0b1838]/80" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 text-white">
          <div className="overline text-[#aedfff] mb-3">Repair Service</div>
          <h1 className="font-display font-black text-5xl lg:text-6xl tracking-tighter max-w-3xl">Broken? Slow? Wet?<br/>We fix it. Fast.</h1>
          <p className="mt-4 text-lg text-slate-300 max-w-xl">Certified technicians. Original parts. 90-day repair warranty. Most repairs done in under 2 hours.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-4 gap-6" data-testid="repairs-benefits">
        {[
          { icon: Clock, t: "2-hour repairs", d: "Most issues fixed same-day." },
          { icon: ShieldCheck, t: "90-day warranty", d: "On parts & labor." },
          { icon: Award, t: "Certified pros", d: "Trained on every brand." },
          { icon: Wrench, t: "Original parts", d: "OEM-quality only." },
        ].map((b) => {
          const Icon = b.icon;
          return (
            <div key={b.t} className="p-6 border border-slate-200 rounded-xl bg-white">
              <Icon className="text-[#1a2d5e]" size={26} />
              <div className="mt-3 font-display font-bold text-lg">{b.t}</div>
              <div className="text-sm text-slate-500 mt-1">{b.d}</div>
            </div>
          );
        })}
      </section>

      <section className="max-w-3xl mx-auto px-6 lg:px-10 pb-20" data-testid="repairs-form-section">
        <div className="overline">Book a repair</div>
        <h2 className="font-display font-black text-3xl mt-2 text-[#0b1838]">Tell us what's broken</h2>
        <form onSubmit={submit} className="mt-8 grid gap-4" data-testid="repair-request-form">
          <input required placeholder="Your name" data-testid="repair-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-lg" />
          <div className="grid sm:grid-cols-2 gap-4">
            <input required placeholder="Phone" data-testid="repair-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-lg" />
            <input type="email" placeholder="Email (optional)" data-testid="repair-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-lg" />
          </div>
          <input required placeholder="Device (e.g. iPhone 13 — cracked screen)" data-testid="repair-device" value={form.device} onChange={(e) => setForm({ ...form, device: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-lg" />
          <textarea rows={4} placeholder="Describe the issue…" data-testid="repair-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-lg" />
          <button disabled={submitting} data-testid="repair-submit" type="submit" className="px-6 py-4 bg-[#1a2d5e] text-white font-semibold rounded-lg hover:bg-[#0b1838] transition-colors disabled:opacity-60">{submitting ? "Sending…" : "Request Repair"}</button>
        </form>
      </section>
    </div>
  );
}
